import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { PartnerSendPicker } from "../request-form/PartnerSendPicker";
import { formatDateTime } from "../../lib/format";
import { toast } from "sonner";
import { Send, Loader2, Mail } from "lucide-react";

const ORD = ["Primo", "Secondo", "Terzo", "Quarto", "Quinto", "Sesto", "Settimo", "Ottavo", "Nono", "Decimo"];
const ordinal = (n) => `${ORD[n - 1] || `${n}°`} invio`;

const RinviaDialog = ({ open, onOpenChange, req, partners, onDone }) => {
  const [lists, setLists] = useState([]);
  const [ids, setIds] = useState([]);
  const [listIds, setListIds] = useState([]);
  const [includeAlready, setIncludeAlready] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { api.get("/partner-lists").then(({ data }) => setLists(data)).catch(() => {}); setIds([]); setListIds([]); setIncludeAlready(false); } }, [open]);
  const already = req.contacted_partner_ids || [];
  const newCount = ids.filter((i) => !already.includes(i)).length;

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/requests/${req.id}/invii`, { partner_ids: ids, list_ids: listIds, include_already: includeAlready });
      toast.success(`${ordinal(data.invii.length)} avviato`, { description: "Le email partono in sequenza; esito nel Registro Invii." });
      onOpenChange(false); onDone(data);
    } catch (err) { toast.error("Rinvio non riuscito", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">Rinvia il servizio ad altri partner</DialogTitle>
          <DialogDescription>Seleziona liste o singoli partner. I partner già contattati sono evidenziati e non ricevono di nuovo la mail, salvo scelta esplicita.</DialogDescription>
        </DialogHeader>
        <PartnerSendPicker partners={partners} lists={lists} selectedIds={ids} setSelectedIds={setIds} selectedListIds={listIds} setSelectedListIds={setListIds} alreadyIds={already} testPrefix="rinvio" />
        <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2.5">
          <div>
            <p className="text-sm font-medium text-slate-800">Reinvia anche ai già contattati</p>
            <p className="text-xs text-slate-500">{includeAlready ? `${ids.length} partner riceveranno la mail` : `${newCount} nuovi partner riceveranno la mail`}</p>
          </div>
          <Switch data-testid="rinvio-include-already" checked={includeAlready} onCheckedChange={setIncludeAlready} />
        </div>
        <DialogFooter>
          <Button data-testid="rinvio-confirm" onClick={send} disabled={busy || ids.length === 0} className="gap-2 w-full">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Conferma invio
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const InviiCard = ({ req, partners, onUpdated }) => {
  const [open, setOpen] = useState(false);
  const invii = req.invii || [];
  const canResend = req.status === "pubblicata" && !req.assigned_partner_id;
  if (!invii.length && !canResend) return null;
  return (
    <Card className="p-5 border-slate-200 mb-6" data-testid="invii-card">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-primary" /><h3 className="font-semibold text-slate-800 text-sm">Invii ai partner <span className="text-slate-400 font-normal">· {(req.contacted_partner_ids || []).length} contattati</span></h3></div>
        {canResend && <Button size="sm" variant="outline" data-testid="rinvia-button" className="gap-1.5" onClick={() => setOpen(true)}><Send className="h-3.5 w-3.5" /> Rinvia ad altri partner</Button>}
      </div>
      {invii.length === 0 ? <p className="text-sm text-slate-500">Nessun invio registrato.</p> : (
        <div className="space-y-3">
          {invii.map((inv) => (
            <div key={inv.n} data-testid={`invio-${inv.n}`} className="rounded-md border border-slate-200 bg-slate-50/60 px-4 py-3">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs mb-2">
                <span className="font-semibold text-slate-800">{ordinal(inv.n)}</span>
                <span className="text-slate-500">{formatDateTime(inv.at)}</span>
                {inv.by && <span className="text-slate-400">· {inv.by}</span>}
                <span className="rounded-full bg-white border border-slate-200 px-2 py-0.5 text-slate-600">{inv.mode === "all" ? "Tutti i partner" : "Lista partner"} · {inv.partners?.length || 0}</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(inv.partners || []).map((p) => <span key={p.id} className="inline-flex items-center rounded-full bg-white border border-slate-200 px-2.5 py-0.5 text-xs text-slate-700">{p.name}</span>)}
              </div>
            </div>
          ))}
        </div>
      )}
      <RinviaDialog open={open} onOpenChange={setOpen} req={req} partners={partners} onDone={onUpdated} />
    </Card>
  );
};
